import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Brain, BarChart3, Sliders, Shield, ScrollText, Loader2, Send,
  TrendingUp, MessageCircle, Zap, Target, Megaphone, Trash2, Plus,
  DollarSign, ShoppingCart, ArrowUpRight, Activity, Users
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const COLORS = ['#D97706', '#2563EB', '#10B981', '#EF4444', '#7C3AED', '#EC4899', '#0891B2'];

export default function AgentControlPage() {
  const { authHeaders, API, activeBranch } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [personality, setPersonality] = useState({ formality: 30, detail: 50, enthusiasm: 60, sales_push: 40, greeting: '', farewell: '', custom_phrases: [] });
  const [rules, setRules] = useState([]);
  const [mktInput, setMktInput] = useState('');
  const [mktMessages, setMktMessages] = useState([]);
  const [mktLoading, setMktLoading] = useState(false);
  const [mktSession] = useState(() => `mkt-${Date.now()}`);
  const [loading, setLoading] = useState(true);
  const [newPhrase, setNewPhrase] = useState('');
  const [perfData, setPerfData] = useState(null);
  const [customerInsights, setCustomerInsights] = useState(null);

  useEffect(() => { if (activeBranch) fetchAll(); }, [activeBranch]);

  const fetchAll = async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const bid = activeBranch.id;
      const [statsR, logsR, persR, rulesR, perfR, insightsR] = await Promise.all([
        axios.get(`${API}/agent/stats?branch_id=${bid}`, authHeaders()),
        axios.get(`${API}/agent/logs?branch_id=${bid}&limit=30`, authHeaders()),
        axios.get(`${API}/agent/personality?branch_id=${bid}&agent_type=order`, authHeaders()),
        axios.get(`${API}/agent/rules?branch_id=${bid}`, authHeaders()),
        axios.get(`${API}/agent/performance?branch_id=${bid}`, authHeaders()),
        axios.get(`${API}/agent/customer-insights?branch_id=${bid}`, authHeaders()).catch(() => ({ data: null })),
      ]);
      setStats(statsR.data);
      setLogs(logsR.data);
      setPersonality(persR.data);
      setRules(rulesR.data);
      setPerfData(perfR.data);
      setCustomerInsights(insightsR.data);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const savePersonality = async () => {
    try {
      await axios.post(`${API}/agent/personality?branch_id=${activeBranch.id}&agent_type=order`, personality, authHeaders());
      toast.success(t('agents.personalitySaved'));
    } catch { toast.error(t('agents.saveFailed')); }
  };

  const addRule = async (condition, action_text) => {
    try {
      const res = await axios.post(`${API}/agent/rules?branch_id=${activeBranch.id}`, { condition, action_text }, authHeaders());
      setRules(prev => [...prev, res.data]);
      toast.success(t('agents.ruleSaved'));
    } catch { toast.error(t('agents.saveFailed')); }
  };

  const deleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/agent/rules/${ruleId}`, authHeaders());
      setRules(prev => prev.filter(r => r.id !== ruleId));
    } catch { toast.error(t('agents.saveFailed')); }
  };

  const sendMktMessage = async () => {
    if (!mktInput.trim() || mktLoading) return;
    const msg = mktInput.trim();
    setMktInput('');
    setMktMessages(prev => [...prev, { role: 'user', content: msg }]);
    setMktLoading(true);
    try {
      const res = await axios.post(`${API}/agent/marketing/chat?branch_id=${activeBranch.id}&message=${encodeURIComponent(msg)}&session_id=${mktSession}`, {}, authHeaders());
      setMktMessages(prev => [...prev, { role: 'assistant', content: res.data.text, actions: res.data.actions_executed }]);
    } catch {
      setMktMessages(prev => [...prev, { role: 'assistant', content: t('agents.chatError') }]);
    } finally { setMktLoading(false); }
  };

  if (!activeBranch) return <ModuleLayout moduleKey="ai"><div className="flex items-center justify-center min-h-[60vh] text-muted-foreground">{t('agents.selectBranch')}</div></ModuleLayout>;
  if (loading) return <ModuleLayout moduleKey="ai"><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></ModuleLayout>;

  return (
    <ModuleLayout moduleKey="ai">
      <div className="space-y-6" data-testid="agent-control-page">
        <div>
          <h1 className="font-bold text-xl sm:text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            <Brain className="w-6 h-6 text-primary" /> {t('agents.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('agents.subtitle')}</p>
        </div>

        <Tabs defaultValue="performance" className="w-full">
          <TabsList className="flex w-full overflow-x-auto no-scrollbar mb-4 h-auto flex-nowrap">
            <TabsTrigger value="performance" className="flex-shrink-0 text-xs sm:text-sm"><Activity className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{t('agents.performance')}</span></TabsTrigger>
            <TabsTrigger value="stats" className="flex-shrink-0 text-xs sm:text-sm"><BarChart3 className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{t('agents.stats')}</span></TabsTrigger>
            <TabsTrigger value="personality" className="flex-shrink-0 text-xs sm:text-sm"><Sliders className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{t('agents.personality')}</span></TabsTrigger>
            <TabsTrigger value="rules" className="flex-shrink-0 text-xs sm:text-sm"><Shield className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{t('agents.rules')}</span></TabsTrigger>
            <TabsTrigger value="logs" className="flex-shrink-0 text-xs sm:text-sm"><ScrollText className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{t('agents.logs')}</span></TabsTrigger>
            <TabsTrigger value="marketing" className="flex-shrink-0 text-xs sm:text-sm"><Megaphone className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{t('agents.marketing')}</span></TabsTrigger>
            <TabsTrigger value="insights" className="flex-shrink-0 text-xs sm:text-sm"><Users className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">{t('agents.customerInsights')}</span></TabsTrigger>
          </TabsList>

          {/* Performance Tab */}
          <TabsContent value="performance">
            {perfData ? (
              <div className="space-y-4" data-testid="performance-tab">
                {/* KPI Cards Row 1 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: t('agents.totalRevenue'), value: `${perfData.overview.total_revenue} ${t('agents.sar')}`, icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                    { label: t('agents.avgOrderValue'), value: `${perfData.overview.avg_order_value} ${t('agents.sar')}`, icon: TrendingUp, color: 'text-blue-500', bg: 'bg-blue-500/10' },
                    { label: t('agents.upsellingRate'), value: `${perfData.overview.upselling_rate}%`, icon: ArrowUpRight, color: 'text-purple-500', bg: 'bg-purple-500/10' },
                    { label: t('agents.avgItemsPerOrder'), value: perfData.overview.avg_items_per_order, icon: ShoppingCart, color: 'text-amber-500', bg: 'bg-amber-500/10' },
                  ].map((kpi, i) => {
                    const Icon = kpi.icon;
                    return (
                      <Card key={i} className="border border-border/50" data-testid={`kpi-${i}`}>
                        <CardContent className="p-4">
                          <div className={`w-9 h-9 rounded-xl ${kpi.bg} flex items-center justify-center mb-3`}>
                            <Icon className={`w-4.5 h-4.5 ${kpi.color}`} />
                          </div>
                          <p className="text-lg sm:text-xl font-bold text-foreground">{kpi.value}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{kpi.label}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* KPI Cards Row 2 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: t('agents.conversations'), value: perfData.overview.total_conversations, sub: `${perfData.overview.week_conversations} ${t('agents.weekConversations')}` },
                    { label: t('agents.ordersCount'), value: perfData.overview.total_orders, sub: `${perfData.overview.week_orders} ${t('agents.weekOrders')}` },
                    { label: t('agents.conversionRate'), value: `${perfData.overview.conversion_rate}%` },
                    { label: t('agents.maxOrderValue'), value: `${perfData.overview.max_order_value} ${t('agents.sar')}` },
                  ].map((kpi, i) => (
                    <Card key={i} className="border border-border/50">
                      <CardContent className="p-4">
                        <p className="text-xl font-bold">{kpi.value}</p>
                        <p className="text-[11px] text-muted-foreground">{kpi.label}</p>
                        {kpi.sub && <p className="text-[10px] text-primary mt-1">{kpi.sub}</p>}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Top Items */}
                  <Card data-testid="top-items-chart">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{t('agents.topOrderedItems')}</CardTitle></CardHeader>
                    <CardContent>
                      {perfData.top_items?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={perfData.top_items} layout="vertical">
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                            <Tooltip formatter={(v) => [`${v}`, t('agents.countLabel')]} />
                            <Bar dataKey="count" fill="#D97706" radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <p className="text-sm text-muted-foreground text-center py-10">{t('agents.noDataYet')}</p>}
                    </CardContent>
                  </Card>

                  {/* Category Breakdown */}
                  <Card data-testid="category-chart">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{t('agents.categoryBreakdown')}</CardTitle></CardHeader>
                    <CardContent>
                      {perfData.categories?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={perfData.categories} dataKey="revenue" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} style={{ fontSize: 10 }}>
                              {perfData.categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => [`${v} ${t('agents.sar')}`, t('agents.revenueLabel')]} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : <p className="text-sm text-muted-foreground text-center py-10">{t('agents.noData')}</p>}
                    </CardContent>
                  </Card>
                </div>

                {/* Hourly Activity + Intents */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card data-testid="hourly-chart">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{t('agents.hourlyActivity')}</CardTitle></CardHeader>
                    <CardContent>
                      {perfData.hourly_activity?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={perfData.hourly_activity}>
                            <XAxis dataKey="hour" tick={{ fontSize: 9 }} />
                            <YAxis tick={{ fontSize: 9 }} />
                            <Tooltip formatter={(v) => [v, t('agents.conversations')]} labelFormatter={(l) => `${t('agents.hour')} ${l}:00`} />
                            <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <p className="text-sm text-muted-foreground text-center py-10">{t('agents.noDataYet')}</p>}
                    </CardContent>
                  </Card>

                  {/* Top Items Revenue Table */}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{t('agents.revenueLabel')} - {t('agents.topOrderedItems')}</CardTitle></CardHeader>
                    <CardContent>
                      {perfData.top_items?.length > 0 ? (
                        <div className="space-y-1.5 max-h-[180px] overflow-y-auto custom-scrollbar">
                          {perfData.top_items.map((item, i) => (
                            <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-muted/50 transition-colors" data-testid={`top-item-${i}`}>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}</span>
                                <span className="text-xs font-medium text-foreground">{item.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-[11px]">
                                <span className="text-muted-foreground">{item.count}x</span>
                                <span className="font-bold text-primary">{item.revenue} {t('agents.sar')}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-muted-foreground text-center py-10">{t('agents.noData')}</p>}
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : <p className="text-center text-muted-foreground py-10">{t('agents.noDataYet')}</p>}
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            {stats && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: t('agents.conversations'), value: stats.total_conversations, icon: MessageCircle, color: 'text-primary' },
                    { label: t('agents.conversionRate'), value: `${stats.conversion_rate}%`, icon: Target, color: 'text-emerald-500' },
                    { label: t('agents.ordersLabel'), value: stats.orders_from_chat, icon: TrendingUp, color: 'text-blue-500' },
                    { label: t('agents.toolsUsed'), value: stats.tools_usage?.length || 0, icon: Zap, color: 'text-purple-500' },
                  ].map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <Card key={i} className="bg-card border border-border/50">
                        <CardContent className="p-4">
                          <Icon className={`w-5 h-5 ${s.color} mb-2`} />
                          <p className="text-2xl font-bold">{s.value}</p>
                          <p className="text-xs text-muted-foreground">{s.label}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{t('agents.topIntents')}</CardTitle></CardHeader>
                    <CardContent>
                      {stats.top_intents?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={stats.top_intents.slice(0, 6)} layout="vertical">
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis type="category" dataKey="intent" tick={{ fontSize: 10 }} width={120} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#D97706" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <p className="text-sm text-muted-foreground text-center py-10">{t('agents.noDataYet')}</p>}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{t('agents.toolsChart')}</CardTitle></CardHeader>
                    <CardContent>
                      {stats.tools_usage?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={stats.tools_usage} dataKey="count" nameKey="tool" cx="50%" cy="50%" outerRadius={70} label={({ tool }) => tool} style={{ fontSize: 9 }}>
                              {stats.tools_usage.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : <p className="text-sm text-muted-foreground text-center py-10">{t('agents.noData')}</p>}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Personality Tab */}
          <TabsContent value="personality">
            <Card>
              <CardContent className="p-6 space-y-5">
                <h3 className="font-semibold flex items-center gap-2"><Sliders className="w-4 h-4 text-primary" /> {t('agents.personalityStudio')}</h3>
                {[
                  { key: 'formality', label: t('agents.formality'), left: t('agents.casual'), right: t('agents.formal') },
                  { key: 'detail', label: t('agents.detail'), left: t('agents.brief'), right: t('agents.detailed') },
                  { key: 'enthusiasm', label: t('agents.enthusiasm'), left: t('agents.calm'), right: t('agents.excited') },
                  { key: 'sales_push', label: t('agents.salesPush'), left: t('agents.soft'), right: t('agents.direct') },
                ].map(s => (
                  <div key={s.key}>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>{s.left}</span><span className="font-semibold text-foreground">{s.label}: {personality[s.key]}%</span><span>{s.right}</span>
                    </div>
                    <input type="range" min={0} max={100} value={personality[s.key]} onChange={e => setPersonality(p => ({ ...p, [s.key]: parseInt(e.target.value) }))}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{ accentColor: '#D97706' }} data-testid={`slider-${s.key}`} />
                  </div>
                ))}
                <div>
                  <Label className="text-xs mb-1 block">{t('agents.greeting')}</Label>
                  <Input value={personality.greeting} onChange={e => setPersonality(p => ({ ...p, greeting: e.target.value }))} placeholder={t('agents.greetingPlaceholder')} className="h-9 text-sm" data-testid="greeting-input" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">{t('agents.farewell')}</Label>
                  <Input value={personality.farewell} onChange={e => setPersonality(p => ({ ...p, farewell: e.target.value }))} placeholder={t('agents.farewellPlaceholder')} className="h-9 text-sm" data-testid="farewell-input" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">{t('agents.customPhrases')}</Label>
                  <div className="flex gap-2 flex-wrap mb-2">
                    {(personality.custom_phrases || []).map((p, i) => (
                      <Badge key={i} variant="secondary" className="gap-1">{p}
                        <button onClick={() => setPersonality(prev => ({ ...prev, custom_phrases: prev.custom_phrases.filter((_, j) => j !== i) }))} className="text-destructive">×</button>
                      </Badge>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={newPhrase} onChange={e => setNewPhrase(e.target.value)} placeholder={t('agents.addPhrase')} className="h-8 text-xs flex-1" />
                    <Button size="sm" className="h-8 rounded-full" onClick={() => { if (newPhrase.trim()) { setPersonality(p => ({ ...p, custom_phrases: [...(p.custom_phrases || []), newPhrase.trim()] })); setNewPhrase(''); } }}>
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <Button onClick={savePersonality} className="w-full rounded-full" data-testid="save-personality-btn">{t('agents.savePersonality')}</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rules Tab */}
          <TabsContent value="rules">
            <Card>
              <CardContent className="p-6 space-y-4">
                <h3 className="font-semibold flex items-center gap-2"><Shield className="w-4 h-4 text-primary" /> {t('agents.rulesEngine')}</h3>
                <div className="space-y-2">
                  {rules.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm">
                      <span>{r.action_text}</span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => deleteRule(r.id)} data-testid={`delete-rule-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4 space-y-3">
                  <p className="text-xs font-semibold">{t('agents.addRule')}</p>
                  {[
                    { label: t('agents.rulePreset1'), cond: { type: 'cart_above', amount: 100 }, act: 'إذا كانت السلة أكثر من 100 ريال، اقترح خصم 10% على الطلب' },
                    { label: t('agents.rulePreset2'), cond: { type: 'returning_customer' }, act: 'إذا كان عميل عائد، رحب به باسمه واقترح المعتاد' },
                    { label: t('agents.rulePreset3'), cond: { type: 'time_after', hour: 22 }, act: 'بعد الساعة 10 مساءً، أخبر العميل أن التوصيل قد يتأخر 15 دقيقة' },
                  ].map((preset, i) => (
                    <Button key={i} variant="outline" size="sm" className="w-full justify-start text-xs h-auto py-2 px-3" onClick={() => addRule(preset.cond, preset.act)} data-testid={`add-rule-preset-${i}`}>
                      <Plus className="w-3 h-3 mr-2 flex-shrink-0" /> {preset.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <Card>
              <CardContent className="p-4">
                <div className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                  {logs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-10 text-sm">{t('agents.noLogs')}</p>
                  ) : logs.map((log, i) => (
                    <div key={i} className="p-3 bg-muted/30 rounded-lg text-sm border border-border/30" data-testid={`log-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-[10px]">{log.detected_intent}</Badge>
                        <span className="text-[10px] text-muted-foreground">{new Date(log.created_at).toLocaleString('ar-SA')}</span>
                      </div>
                      <p className="text-xs text-foreground mb-1"><strong>{t('agents.customerLabel')}</strong> {log.user_message}</p>
                      <div className="flex gap-1 flex-wrap">
                        {log.tools_used?.map((tool, j) => (
                          <Badge key={j} className="text-[10px] bg-primary/10 text-primary">{tool}</Badge>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1 truncate">{log.response_preview}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing">
            <Card className="overflow-hidden" style={{ height: 'calc(100vh - 280px)' }}>
              <CardHeader className="pb-2 border-b">
                <CardTitle className="text-sm flex items-center gap-2"><Megaphone className="w-4 h-4 text-primary" /> {t('agents.marketingAgent')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0 h-full flex flex-col">
                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                  {mktMessages.length === 0 && (
                    <div className="text-center py-8">
                      <Megaphone className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
                      <p className="text-sm text-muted-foreground">{t('agents.askMarketing')}</p>
                      <div className="flex flex-wrap gap-2 justify-center mt-3">
                        {[t('agents.mktSuggestion1'), t('agents.mktSuggestion2'), t('agents.mktSuggestion3')].map((s, i) => (
                          <button key={i} onClick={() => { setMktInput(s); }} className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">{s}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {mktMessages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'} gap-2`}>
                      <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed whitespace-pre-line ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                        {m.content}
                        {m.actions?.length > 0 && <div className="flex gap-1 mt-1">{m.actions.map((a, j) => <Badge key={j} className="text-[9px]">{a}</Badge>)}</div>}
                      </div>
                    </div>
                  ))}
                  {mktLoading && <div className="flex justify-end"><div className="bg-muted px-3 py-2 rounded-xl"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div></div>}
                </div>
                <div className="p-3 border-t">
                  <div className="flex gap-2">
                    <Input value={mktInput} onChange={e => setMktInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendMktMessage(); }}
                      placeholder={t('agents.mktPlaceholder')} className="flex-1 h-10 text-sm" disabled={mktLoading} data-testid="mkt-chat-input" />
                    <Button onClick={sendMktMessage} disabled={!mktInput.trim() || mktLoading} className="h-10 px-4 rounded-full" data-testid="mkt-chat-send">
                      {mktLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customer Insights Tab */}
          <TabsContent value="insights">
            <div className="space-y-4" data-testid="customer-insights-tab">
              {/* Summary Cards */}
              {customerInsights?.summary && (
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: t('agents.totalProfiles'), value: customerInsights.summary.total_profiles, color: 'text-blue-500', bg: 'bg-blue-500/10' },
                    { label: t('agents.returningCustomers'), value: customerInsights.summary.returning_customers, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
                    { label: t('agents.drinkLovers'), value: customerInsights.summary.drink_lovers, color: 'text-purple-500', bg: 'bg-purple-500/10' },
                  ].map((s, i) => (
                    <Card key={i} className="border-0 shadow-sm" data-testid={`insight-summary-${i}`}>
                      <CardContent className="p-4 text-center">
                        <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                        <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Profiles List */}
              {customerInsights?.profiles?.length > 0 ? (
                <div className="space-y-3">
                  {customerInsights.profiles.map((profile, i) => {
                    const prefBadges = [];
                    if (profile.always_orders_drink) prefBadges.push({ key: 'prefDrink', color: 'bg-blue-500/10 text-blue-600' });
                    if (profile.spice_preference === 'spicy') prefBadges.push({ key: 'prefSpicy', color: 'bg-red-500/10 text-red-600' });
                    if (profile.spice_preference === 'mild') prefBadges.push({ key: 'prefMild', color: 'bg-green-500/10 text-green-600' });
                    if (profile.price_sensitivity === 'budget') prefBadges.push({ key: 'prefBudget', color: 'bg-amber-500/10 text-amber-600' });
                    if (profile.price_sensitivity === 'premium') prefBadges.push({ key: 'prefPremium', color: 'bg-purple-500/10 text-purple-600' });
                    if (profile.avg_order_size === 'large') prefBadges.push({ key: 'prefLargeOrder', color: 'bg-orange-500/10 text-orange-600' });
                    if (profile.avg_order_size === 'small') prefBadges.push({ key: 'prefSmallOrder', color: 'bg-slate-500/10 text-slate-600' });
                    const timeMap = { breakfast: 'prefBreakfast', lunch: 'prefLunch', afternoon: 'prefAfternoon', dinner: 'prefDinner' };
                    if (profile.usual_order_time && timeMap[profile.usual_order_time]) prefBadges.push({ key: timeMap[profile.usual_order_time], color: 'bg-cyan-500/10 text-cyan-600' });

                    return (
                      <Card key={i} className="border-0 shadow-sm" data-testid={`customer-profile-${i}`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">{i + 1}</div>
                              <div>
                                <div className="font-medium text-sm">{profile.phone || profile.session_id?.slice(0, 12) || '---'}</div>
                                <div className="text-xs text-muted-foreground">{profile.order_count} {t('agents.ordersCount')} | {profile.total_spent} {t('agents.sar')}</div>
                              </div>
                            </div>
                            {profile.last_order_date && (
                              <div className="text-xs text-muted-foreground">{t('agents.lastOrder')}: {profile.last_order_date.slice(0, 10)}</div>
                            )}
                          </div>
                          {prefBadges.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {prefBadges.map((b, bi) => (
                                <Badge key={bi} variant="secondary" className={`text-[10px] ${b.color}`}>{t(`agents.${b.key}`)}</Badge>
                              ))}
                              {profile.preferred_categories?.length > 0 && profile.preferred_categories.map((cat, ci) => {
                                const catLabels = { main: 'رئيسية', appetizer: 'مقبلات', dessert: 'حلويات', beverage: 'مشروبات' };
                                return <Badge key={`cat-${ci}`} variant="outline" className="text-[10px]">{catLabels[cat] || cat}</Badge>;
                              })}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-8 text-center text-muted-foreground text-sm">
                    <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    {t('agents.noProfiles')}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </ModuleLayout>
  );
}
